import React from 'react'
import { Navigate, Outlet, RouteObject, useOutletContext, useRoutes } from 'react-router-dom'
import type { MeResponse } from '../api/me'
import AppShell, { type AppOutletContext } from './AppShell'
import Inbox from '../pages/Inbox'
import ContactsPage from '../pages/contacts/ContactsPage'
import ContactDetailPage from '../pages/contacts/ContactDetailPage'
import Rounds from '../pages/Rounds'
import Competitions from '../pages/Competitions'
import Surveys from '../pages/Surveys'
import Dashboard from '../pages/Dashboard'
import Settings from '../pages/Settings'
import IntegrationsHome from '../pages/integrations/IntegrationsHome'
import EmailIntegration from '../pages/integrations/EmailIntegration'
import TelegramIntegration from '../pages/integrations/TelegramIntegration'
import SettingsProfilePage from '../pages/settings/Profile'
import StationGeneralSettingsPage from '../pages/settings/StationGeneral'
import StationSettingsPage from '../pages/settings/StationSettingsPage'
import UserManagementPage from '../pages/settings/UserManagementPage'
import IngestKeysPanel from '../pages/settings/prep/IngestKeysPanel'
import ShowClockSettingsPage from '../pages/settings/showClock/ShowClockSettingsPage'
import BillingPage from '../pages/settings/BillingPage'
import Prep from '../pages/Prep'
import YoutubeMp3Downloader from '../pages/tools/YoutubeMp3Downloader'
import YoutubeCreditsPage from '../pages/tools/YoutubeCreditsPage'
import Superadmin from '../pages/Superadmin'
import SuperadminStationsUsers from '../pages/SuperadminStationsUsers'
import GlobalWeatherSettingsPage from '../pages/superadmin/GlobalWeatherSettingsPage'
import YtMp3SettingsPage from '../pages/superadmin/YtMp3SettingsPage'
import PrepCategoryManagerPage from '../pages/superadmin/PrepCategoryManagerPage'
import SuperadminPrepSheetSettingsPage from '../pages/superadmin/PrepSheetSettingsPage'
import PrepExportPage from '../pages/superadmin/PrepExportPage'
import AutoResponderPage from '../pages/inbox/AutoResponderPage'
import ShowClockTemplatesPage from '../pages/showClock/ShowClockTemplatesPage'
import ShowClockDaysPage from '../pages/showClock/ShowClockDaysPage'
import ShowClockLivePage from '../pages/showClock/ShowClockLivePage'
import ShowClockAuditPage from '../pages/showClock/ShowClockAuditPage'
import ManualGrantsPage from '../pages/superadmin/ManualGrantsPage'
import ConsolidationAcceptPage from '../pages/ConsolidationAcceptPage'
import SubscriptionsConsolePage from '../pages/superadmin/SubscriptionsConsolePage'
import ProductManagerPage from '../pages/superadmin/ProductManagerPage'

export interface RouteConfigOptions {
  me: MeResponse
  includeSuperadmin: boolean
  onUpdateMe?: (updater: (prev: MeResponse) => MeResponse) => void
}

const ShowClockRoutes: React.FC = () => {
  const context = useOutletContext<AppOutletContext>()
  return <Outlet context={context} />
}

const ToolsRoutes: React.FC = () => {
  const context = useOutletContext<AppOutletContext>()
  return <Outlet context={context} />
}

export function createAppRoutes({ me, includeSuperadmin, onUpdateMe }: RouteConfigOptions): RouteObject[] {
  const children: RouteObject[] = [
    { index: true, element: <Navigate to="dashboard" replace /> },
    { path: 'inbox', element: <Inbox /> },
    { path: 'inbox/contacts', element: <ContactsPage /> },
    { path: 'inbox/auto-responder', element: <AutoResponderPage /> },
    { path: 'inbox/contacts/:contactId', element: <ContactDetailPage /> },
    { path: 'rounds', element: <Rounds /> },
    { path: 'competitions', element: <Competitions /> },
    { path: 'surveys', element: <Surveys /> },
    { path: 'dashboard', element: <Dashboard /> },
    { path: 'consolidation', element: <ConsolidationAcceptPage /> },
    {
      path: 'show-clock',
      element: <ShowClockRoutes />,
      children: [
        { index: true, element: <Navigate to="live" replace /> },
        { path: 'live', element: <ShowClockLivePage /> },
        { path: 'days', element: <ShowClockDaysPage /> },
        { path: 'templates', element: <ShowClockTemplatesPage /> },
        { path: 'audit', element: <ShowClockAuditPage /> },
      ],
    },
    {
      path: 'settings',
      element: <Settings />,
      children: [
        { index: true, element: <Navigate to="general" replace /> },
        { path: 'general', element: <StationGeneralSettingsPage /> },
        { path: 'station', element: <StationSettingsPage /> },
        { path: 'users', element: <UserManagementPage /> },
        { path: 'billing', element: <BillingPage /> },
        { path: 'show-clock', element: <ShowClockSettingsPage /> },
        { path: 'profile', element: <SettingsProfilePage /> },
        { path: 'integrations', element: <IntegrationsHome /> },
        { path: 'integrations/email', element: <EmailIntegration /> },
        { path: 'integrations/telegram', element: <TelegramIntegration /> },
      ],
    },
    { path: 'prep', element: <Prep /> },
    {
      path: 'tools',
      element: <ToolsRoutes />,
      children: [
        { index: true, element: <Navigate to="youtube-mp3" replace /> },
        { path: 'youtube-mp3', element: <YoutubeMp3Downloader /> },
        { path: 'youtube-credits', element: <YoutubeCreditsPage /> },
      ],
    },
  ]

  if (includeSuperadmin) {
    children.push({
      path: 'superadmin/weather',
      element: <Navigate to="/superadmin/general/weather" replace />,
    })

    children.push({
      path: 'superadmin/ytmp3',
      element: <Navigate to="/superadmin/general/ytmp3" replace />,
    })

    children.push({
      path: 'superadmin/general/subscriptions',
      element: <Navigate to="/superadmin/stations-users/subscriptions" replace />,
    })

    children.push({
      path: 'superadmin/general/grants',
      element: <Navigate to="/superadmin/stations-users/grants" replace />,
    })

    children.push({
      path: 'superadmin/prep',
      element: <SuperadminPrepSheetSettingsPage />,
      children: [
        { index: true, element: <Navigate to="categories" replace /> },
        { path: 'categories', element: <PrepCategoryManagerPage /> },
        { path: 'ingest-keys', element: <IngestKeysPanel /> },
        { path: 'export', element: <PrepExportPage /> },
      ],
    })

    children.push({
      path: 'superadmin',
      children: [
        { index: true, element: <Navigate to="general/weather" replace /> },
        {
          path: 'general',
          element: <Superadmin />,
          children: [
            { index: true, element: <Navigate to="weather" replace /> },
            { path: 'weather', element: <GlobalWeatherSettingsPage /> },
            { path: 'ytmp3', element: <YtMp3SettingsPage /> },
          ],
        },
        {
          path: 'stations-users',
          element: <SuperadminStationsUsers />,
          children: [
            { index: true, element: <Navigate to="subscriptions" replace /> },
            { path: 'subscriptions', element: <SubscriptionsConsolePage /> },
            { path: 'grants', element: <ManualGrantsPage /> },
          ],
        },
        { path: 'products', element: <ProductManagerPage /> },
      ],
    })
  }

  children.push({ path: '*', element: <Navigate to="/dashboard" replace /> })

  return [
    {
      path: '/',
      element: <AppShell me={me} includeSuperadmin={includeSuperadmin} onUpdateMe={onUpdateMe} />,
      children,
    },
  ]
}

export interface AppRoutesProps {
  me: MeResponse
  includeSuperadmin: boolean
  onUpdateMe?: (updater: (prev: MeResponse) => MeResponse) => void
}

export const AppRoutes: React.FC<AppRoutesProps> = ({ me, includeSuperadmin, onUpdateMe }) => {
  const routes = React.useMemo(
    () => createAppRoutes({ me, includeSuperadmin, onUpdateMe }),
    [me, includeSuperadmin, onUpdateMe],
  )
  const element = useRoutes(routes)
  return element
}



